import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash';
import moment from 'moment';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import {
   RequestUserStatusChange,
   SamwinAgentStateChangedByServer,
   SamwinStatusChanged,
   UserStatusInfosChangedByServer,
} from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { UserStatusResetConstants } from 'src/app/store/samwin/samwin-user-status/user-status-reset-constants';
import { Originator } from 'src/generated/signalR/originator';
import { StatusType } from 'src/generated/signalR/status-type';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { UsersService } from 'src/generated/swagger';

export class CurrentUserStatusInfo {
   id: number;
   autoStatusChangeTime: moment.Moment;
   allowedStatusResets: number;
   originator: Originator;
}

export interface SamwinUserStatusStateModel {
   current: CurrentUserStatusInfo;
   agentActive: boolean;
   all: UserStatusInfoRemoteDto[];
}
@State<SamwinUserStatusStateModel>({
   name: 'samwinUserStatus',
   defaults: {
      current: {
         id: UserStatusEnum.UNKNOWN.value,
         autoStatusChangeTime: moment.min(),
         allowedStatusResets: UserStatusResetConstants.NEVER,
         originator: Originator.System,
      },
      agentActive: false,
      all: [],
   },
})
@Injectable()
export class SamwinUserStatusState {
   constructor(private usersService: UsersService) {}

   @Selector([SamwinUserStatusState])
   public static all(state: SamwinUserStatusStateModel) {
      return state?.all ?? [];
   }

   @Selector([SamwinUserStatusState])
   public static current(state: SamwinUserStatusStateModel) {
      return state?.current;
   }

   @Selector([SamwinUserStatusState])
   public static agentActive(state: SamwinUserStatusStateModel) {
      return state?.agentActive;
   }

   @Selector([SamwinUserStatusState.current])
   public static getAllowedStatusResets(currentState: CurrentUserStatusInfo) {
      return currentState.allowedStatusResets;
   }

   @Selector([SamwinUserStatusState.current])
   public static getCurrentStatusId(currentStatus: CurrentUserStatusInfo) {
      return currentStatus.id;
   }

   @Selector([SamwinUserStatusState.current])
   public static getCurrentStateId(currentState: { id: number; autoStatusChangeTime: moment.Moment; allowedStatusResets: number; originator: Originator }) {
      return currentState?.id;
   }

   @Selector([SamwinUserStatusState.all, SamwinUserStatusState.getCurrentStateId])
   public static currentStatusInfo(allStates: UserStatusInfoRemoteDto[], currentId: number): UserStatusInfoRemoteDto {
      return allStates.find((info) => info.id === currentId);
   }

   @Selector([SamwinUserStatusState.all, SamwinUserStatusState.current])
   public static getCurrentStatusInfo(allStates: UserStatusInfoRemoteDto[], currentState: CurrentUserStatusInfo): UserStatusInfoRemoteDto {
      return allStates.find((info) => info.id === currentState.id);
   }

   @Selector([SamwinUserStatusState.all, SamwinUserStatusState.agentActive])
   public static isAgentActive(all: UserStatusInfoRemoteDto[], agentActive: boolean): boolean {
      /*  NOTE: [18572] If the ACD-Extension uses a Hardphone and is not connected on startup,
                        the popup will not show up because the server will send no message on startup.*/
      // Check if state is empty, because Agent default is false.
      if (all.length === 0) {
         return true;
      }
      return agentActive;
   }

   @Selector([SamwinUserStatusState.current])
   public static getNextAutoStatusChangeTime(currentState: CurrentUserStatusInfo): moment.Moment {
      return currentState.autoStatusChangeTime;
   }

   @Selector([SamwinUserStatusState.getNextAutoStatusChangeTime])
   public static isStatusTimerSet(stateUpdateTime: moment.Moment): boolean {
      return stateUpdateTime.diff(moment()) > 0;
   }

   @Selector([SamwinUserStatusState.getCurrentStatusInfo])
   public static isAgentBusy(currentStatusInfo: UserStatusInfoRemoteDto): boolean {
      return (currentStatusInfo?.id ?? UserStatusEnum.LOGGED_OFF.value) === UserStatusEnum.BUSY.value;
   }

   @Selector([SamwinUserStatusState.currentStatusInfo])
   public static isCurrentStatusInfoValid(currentStatusInfo: UserStatusInfoRemoteDto): boolean {
      return !_.isNil(currentStatusInfo);
   }

   @Selector([SamwinUserStatusState.currentStatusInfo])
   public static isUserLoggedOn(userStatus: UserStatusInfoRemoteDto): boolean {
      return !((userStatus?.type ?? StatusType.LOGGED_OFF) === StatusType.LOGGED_OFF);
   }

   @Selector([SamwinUserStatusState.currentStatusInfo])
   public static isAtAfterCallWork(currentStatusInfo: UserStatusInfoRemoteDto): boolean {
      return (currentStatusInfo?.id ?? UserStatusEnum.LOGGED_OFF.value) === UserStatusEnum.ACW.value;
   }

   @Selector([SamwinUserStatusState.currentStatusInfo, SamwinUserStatusState.isStatusTimerSet])
   public static isUserStateAutoACW(currentStatusInfo: UserStatusInfoRemoteDto, isStatusTimerSet: boolean): boolean {
      return currentStatusInfo?.id === UserStatusEnum.ACW.value && isStatusTimerSet;
   }

   @Selector([SamwinUserStatusState.currentStatusInfo])
   public static isUserStateBusy(currentStatusInfo: UserStatusInfoRemoteDto): boolean {
      return currentStatusInfo?.id === UserStatusEnum.BUSY.value;
   }

   @Action(SamwinStatusChanged)
   public samwinStatusChanged(ctx: StateContext<SamwinUserStatusStateModel>, action: SamwinStatusChanged) {
      // Because presence-duration-brick compares this duration with now$, which is up to 500ms old, to determine status expiry,
      // we move 0-duration status changes 500ms into the past to ensure they are never interpreted as future/upcoming states.
      // Otherwise, "a few seconds" is briefly shown as status expiry time in the UI before now$ is refreshed to become greater than this duration.
      const statusEndTime = _.isNil(action.endTime) ? moment().add(-0.5, 'seconds') : moment(action.endTime);

      ctx.patchState({
         current: {
            id: action.status,
            autoStatusChangeTime: statusEndTime,
            allowedStatusResets: action.allowedStatusResets,
            originator: action.originator,
         },
      });
   }

   @Action(UserStatusInfosChangedByServer)
   public userStatusInfosChangedByServer(ctx: StateContext<SamwinUserStatusStateModel>, action: UserStatusInfosChangedByServer) {
      ctx.patchState({
         all: action.userStatusInfos,
      });
   }

   @Action(RequestUserStatusChange)
   public requestUserStatusChange(ctx: StateContext<SamwinUserStatusStateModel>, action: RequestUserStatusChange) {
      return this.usersService.requestUserStatusChange({
         status: action.status,
         originator: action.originator,
      });
   }

   @Action(SamwinAgentStateChangedByServer)
   public samwinAgentStateChangedByServer(ctx: StateContext<SamwinUserStatusStateModel>, action: SamwinAgentStateChangedByServer) {
      ctx.patchState({
         agentActive: action.mode,
      });
   }
}
